import { DateTimeOffset, ReminderInputBlock } from '@coast/schemas';
import { isEqual, partition, sortBy } from 'lodash';
import moment from 'moment';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { Reminder } from '@shared/components/blocks/types';
import { getTextPropsFromTextStyle } from '@shared/components/blocks/utils';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, getAnchorName } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { renderClearButton, renderIcon } from '../input';
import { MBCardInputBlockBase } from '../input/base';
import { MBCardInputBlockReminderAddRow } from './add';
import {
  dateTimeOffsetToReminder,
  format,
  useClearReminderIfNoDateField,
  useReminderOptions,
  useSetDefaultReminder,
} from './hooks';
import { PickerRow, ReminderPickerRow } from './pickerRow';
import { PickerSectionTitle } from './pickerSectionTitle';

function isSelected(
  offset: DateTimeOffset,
  selectedReminders: Reminder[],
  block: ReminderInputBlock,
) {
  const reminder = dateTimeOffsetToReminder(offset, block);
  return selectedReminders.some(r => isEqual(r, reminder));
}

interface Props {
  block: ReminderInputBlock;
}

export function MBCardInputBlockReminder({ block }: Props) {
  const anchorName = getAnchorName('reminder-input-block', block.name);
  const colors = useContext(ColorThemeContext);
  const { values, setFieldValue, setFieldError, errors } = useCardFormContext();
  const isDesktop = useIsDesktop();
  const reminders = values[block.name];
  const dateFieldValue = values[block.dateFieldName];
  const textProps = getTextPropsFromTextStyle(block.textStyle);

  const showNoDateSetError = useCallback(() => {
    setFieldError(block.name, i18n('card.blocks.reminderInput.noDateSetError'));
  }, [setFieldError, block]);

  const clearNoDateSetError = useCallback(() => {
    setFieldError(block.name, undefined);
  }, [setFieldError, block]);

  const toggleNoDateSetError = useCallback(() => {
    if (errors[block.name]?.includes(i18n('card.blocks.reminderInput.noDateSetError'))) {
      clearNoDateSetError();
    } else {
      showNoDateSetError();
    }
  }, [errors, block, showNoDateSetError, clearNoDateSetError]);

  const clearReminders = useCallback(() => {
    setFieldValue(block.name, []);
  }, [setFieldValue, block.name]);
  const toggleSelected = useCallback(
    (offset: DateTimeOffset, selected: boolean) => {
      if (selected) {
        const reminder = dateTimeOffsetToReminder(offset, block);
        setFieldValue(
          block.name,
          sortBy([...reminders, reminder], r => moment.duration(r.duration)),
        );
      } else {
        setFieldValue(
          block.name,
          reminders.filter(
            r => r.duration !== moment.duration(offset.count, offset.unit).toISOString(),
          ),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldValue, reminders, block.name],
  );
  const removeReminderAtIndex = useCallback(
    (index: number) => {
      setFieldValue(block.name, [...reminders.slice(0, index), ...reminders.slice(index + 1)]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reminders],
  );

  const [pickerVisible, setPickerVisible] = useState(false);
  const showPicker = useCallback(() => setPickerVisible(true), []);
  const hidePicker = useCallback(() => setPickerVisible(false), []);

  useSetDefaultReminder(block);
  useClearReminderIfNoDateField(block);

  const options = useReminderOptions(block);
  const [beforeOptions, afterOptions] = useMemo(
    // @ts-expect-error -- strict migration
    () => partition(options, o => o.count <= 0),
    [options],
  );

  const handlePress = useCallback(() => {
    if (!dateFieldValue) {
      toggleNoDateSetError();
    } else {
      showPicker();
    }
  }, [toggleNoDateSetError, showPicker, dateFieldValue]);

  const renderTitleBar = useCallback<() => ReactNode>(() => {
    if (isDesktop) {
      return <AnchoredSheetTitleBar title={block.label} />;
    }

    return (
      <AnchoredSheetTitleBar
        title={block.label}
        rightIcon="exit"
        rightIconColor={colors.foregroundActive}
        onRightIconPress={hidePicker}
      />
    );
  }, [block, colors, hidePicker, isDesktop]);

  if (!block.dateFieldName || !block.recipientFieldName) {
    return (
      <Text {...textProps} danger>
        Warning: cannot render reminder input without a referenced date and recipient field. Did you
        forget to specify block.dateFieldName or block.recipientFieldName?
      </Text>
    );
  }

  return (
    <>
      <View style={{ anchorName }} />
      {!!reminders.length && !!block.label && (
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBBlockLabel label={block.label} />
        </View>
      )}
      {reminders.map((reminder, index) => (
        <MBTouchableOpacity key={index} onPress={handlePress}>
          <View row centerVertically paddingHorizontal={3}>
            {renderIcon(block.icon, block.name)}
            <View flex paddingLeft={2}>
              <Text {...textProps}>{format(moment.duration(reminder.duration))}</Text>
            </View>
            {renderClearButton(block, () => removeReminderAtIndex(index))}
          </View>
        </MBTouchableOpacity>
      ))}
      {!reminders.length && (
        <MBTouchableOpacity onPress={handlePress}>
          <MBCardInputBlockBase block={block}>
            <Text {...textProps} inactive>
              {block.placeholder}
            </Text>
          </MBCardInputBlockBase>
        </MBTouchableOpacity>
      )}
      {!!reminders.length && <MBCardInputBlockReminderAddRow onAdd={showPicker} />}

      {pickerVisible && (
        <AnchoredSheet
          anchorName={anchorName}
          renderTitleBar={renderTitleBar}
          style={{ minWidth: 320 }}
          initialSize="full"
          onClose={hidePicker}
        >
          <ScrollView style={isDesktop && { flex: 1, width: 375 }}>
            <PickerRow
              title={i18n('common.never')}
              selected={!reminders.length}
              onSelect={clearReminders}
            />

            {!!beforeOptions.length && (
              <>
                <PickerSectionTitle title={i18n('card.blocks.reminderInput.beforeDueDate')} />
                {beforeOptions.map((option, index) => (
                  <ReminderPickerRow
                    key={index}
                    offset={option}
                    selected={isSelected(option, reminders, block)}
                    onSelect={toggleSelected}
                  />
                ))}
              </>
            )}
            {!!afterOptions.length && (
              <>
                <PickerSectionTitle title={i18n('card.blocks.reminderInput.ifOverdue')} />
                {afterOptions.map((option, index) => (
                  <ReminderPickerRow
                    key={index}
                    offset={option}
                    selected={isSelected(option, reminders, block)}
                    onSelect={toggleSelected}
                  />
                ))}
              </>
            )}
          </ScrollView>
        </AnchoredSheet>
      )}
    </>
  );
}
